import React, { useState, useEffect, useMemo } from "react"
import { Card } from "ui/Card"
import { Form, Input, Button, Flex, Select } from "antd"
import { AddCategoryModal } from "../AddCategoryModal"
import { RequestMarketplaceTagModal } from "ui/RequestMarketplaceTagModal"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { colors } from "themes/variables"
import { SelectTag } from "ui/SelectTag"
import { getMarketplaceTags } from "store/trips"

const { TextArea } = Input

export const ExperienceInfo = ({ form, onDescriptionChange }) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isShowRequestTagModal, setIsShowRequestTagModal] = useState(false)
  const [showAiModal, setShowAiModal] = useState(false)
  const [descriptionValue, setDescriptionValue] = useState("")
  const [profileDescription, setProfileDescription] = useState("")
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const { data: categoriesData } = useSelector((state) => state.trips.categories)
  const { data: marketplaceTagsData, loading: isMarketplaceTagsLoading } = useSelector(
    (state) => state.trips.marketplaceTags
  )

  const categoryOptions = useMemo(
    () => (categoriesData?.categories || []).map((cat) => ({ value: cat.name, label: cat.name })),
    [categoriesData]
  )

  const marketplaceTagOptions = useMemo(
    () => (marketplaceTagsData || []).map((tag) => ({ value: tag.id, label: tag.name })),
    [marketplaceTagsData]
  )

  useEffect(() => {
    dispatch(getMarketplaceTags())
  }, [dispatch])

  useEffect(() => {
    form.setFieldsValue({ description: descriptionValue })
    onDescriptionChange?.()
  }, [descriptionValue, form])

  useEffect(() => {
    setDescriptionValue(form.getFieldValue("description") || "")
  }, [form.getFieldValue("description")])

  return (
    <>
      <Card title={t("experiences.details.experience_info")} className="mb-xl">
        <Form.Item
          name="categories"
          label={t("experiences.details.labels.category")}
        >
          <Select
            mode="tags"
            showSearch
            placeholder={t("experiences.details.select_category_placeholder")}
            style={{ width: "100%" }}
            options={categoryOptions}
            optionLabelProp="label"
            optionFilterProp="label"
            tagRender={SelectTag}
            filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>

        <div className="mb-m mt-xs sm:text-center">
          <span className="text-sm">
            {t("experiences.details.no_category_info")}{" "}
            <Button type="link" onClick={() => setIsModalVisible(true)} style={{ padding: 0 }}>
              {t("experiences.details.request_category")}
            </Button>
          </span>
        </div>

        <Form.Item
          name="marketplace_tags"
          label={t("experiences.details.labels.marketplace_tags")}
          rules={[{ required: true, message: t("experiences.details.errors.marketplace_tags_required") }]}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder={t("experiences.details.placeholders.select_tags")}
            options={marketplaceTagOptions}
            loading={isMarketplaceTagsLoading}
            optionFilterProp="label"
            tagRender={SelectTag}
          />
        </Form.Item>

        <div className="mb-m mt-xs sm:text-center">
          <span className="text-sm">
            {t("experiences.details.no_tag_info")}{" "}
            <Button type="link" onClick={() => setIsShowRequestTagModal(true)} style={{ padding: 0 }}>
              {t("experiences.details.request_tag")}
            </Button>
          </span>
        </div>

        <Form.Item
          name="title"
          label={t("experiences.details.labels.listing_title")}
          rules={[{ required: true, message: t("experiences.details.listing_title_error") }]}
        >
          <Input placeholder={t("experiences.details.listing_title_placeholder")} />
        </Form.Item>

        <Flex justify="flex-end" className="mb-s">
          <Button
            type="primary"
            icon={
              <Icon
                name="aiEnhancement"
                color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled}
                height={20}
                width={20}
              />
            }
            onClick={() => setShowAiModal(true)}
            disabled={descriptionValue.length < 25}
          >
            {t("client.enhance_with_ui")}
          </Button>
        </Flex>

        <Form.Item
          name="description"
          label={t("global.description")}
          rules={[{ required: true, message: t("pro.ai_enhancement.description_too_short") }]}
        >
          <TextArea
            placeholder={t("experiences.details.description_placeholder")}
            rows={4}
            onChange={(e) => setDescriptionValue(e.target.value)}
            value={descriptionValue}
          />
        </Form.Item>
      </Card>

      <AddCategoryModal isVisible={isModalVisible} onCancel={() => setIsModalVisible(false)} handleClose={() => setIsModalVisible(false)} />
      <RequestMarketplaceTagModal isVisible={isShowRequestTagModal} onCancel={() => setIsShowRequestTagModal(false)} />

      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => setShowAiModal(false)}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription("")
          }}
          text={t("client.experience_content")}
          modalTitle={t("client.experiences_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("client.refinement_tooltip")}
          improveWithAITooltipTitle={t("client.improve_with_ai_tooltip_for_experiences")}
        />
      )}
    </>
  )
}

ExperienceInfo.propTypes = {
  form: PropTypes.object,
  onDescriptionChange: PropTypes.func
}
